import { format } from "date-fns";

/**
 * Extract the day from a given date.
 * @param {Date} date - date
 * @returns {string} day
 *
 * ## Example
 * ```typescript
 * leadingZeroDay('2022-04-01T13:00:00.000Z') // => 1
 * ```
 */
export const dayOfMonthFormat = (date: Date): string => format(date, "d");

/**
 * Extract the short month name from a given date.
 * @param {Date} date - date
 * @returns {string} month
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => Apr
 * ```
 */
export const shortMonthFormat = (date: Date): string => format(date, "MMM");

/**
 * Extract the month from a given date.
 * @param {Date} date - date
 * @returns {string} month
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => April
 * ```
 */
export const monthFormat = (date: Date): string => format(date, "MMMM");

/**
 * Format the given date to be in a short date format.
 * @param {Date} date - date
 * @returns {string} hortten date
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => Apr 12, 2022
 * ```
 */
export const shortDate = (date: Date): string => format(date, "PP");

/**
 * Format the given date to be in a long date format.
 * @param {Date} date - date
 * @returns {string} shortten date
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => April 12th, 2022
 * ```
 */
export const longDate = (date: Date): string => format(date, "PPP");

/**
 * Format the given date to be in a long localized date.
 * @param {Date} date - date
 * @returns {string} shortten date
 *
 * ## Example
 * ```typescript
 * monthFormat('2022-04-12T13:00:00.000Z') // => 12/04/2022
 * ```
 */
export const longLocalizedDate = (date: Date): string => format(date, "P");

export function formatToFilenameDate(date: Date) {
  return format(date, "yyyy-MM-dd_HH-mm");
}

export function formatSecondsToTime(seconds: number) {
  // Format the Date object to get minutes and seconds
  const formattedTime = format(new Date(seconds * 1000), "m:ss");
  return formattedTime;
}

type LongPrettifiedDateArgs = {
  locale?: string;
};

export function longPrettifiedDate(
  date: Date,
  { locale = "en-US" }: LongPrettifiedDateArgs
) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeStyle: "medium",
  }).format(date);
}

const DAY_IN_MILLISECONDS = 1000 * 3600 * 24;
const DAYS_IN_WEEK = 7;

// A Map to hold all our required locales formatters.
const localizedRTFMap = new Map<string, Intl.RelativeTimeFormat>();

/**
 * Returns an Intl.RelativeTimeFormat object for the specified locale.
 * In case the locale does not exist, the function will initialize its
 * formatter and add it to the existing map.
 * @param locale - The locale string.
 * @returns The Intl.RelativeTimeFormat object for the specified locale.
 */
function getRtf(locale: string): Intl.RelativeTimeFormat {
  let formatter = localizedRTFMap.get(locale);

  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    localizedRTFMap.set(locale, formatter);
  }

  return formatter;
}

/**
 * Returns a new Date set to midnight (00:00:00) for the given date.
 * @param date - date.
 * @returns A new Date object set to midnight.
 */
function getMidnightDate(date: Date): Date {
  const newDate = new Date(date.getTime());
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}

/**
 * Localize and format the date relative to today following these rules:
 *  1) Within a week - "Yesterday" / "x days ago" (relative format)
 *  2) Same calendar year - "Month(short) day" (e.g. Dec 12)
 *  3) Different year - locale date (e.g. dd/mm/yyyy)
 * @param date - date
 * @param locale - locale (defaults to "en-US")
 * @returns relative date
 *
 * ## Example
 * ```typescript
 * getRelativeDate(yesterday, 'en-US') // => Yesterday
 * ```
 */
export function getRelativeDate(date: Date, locale = "en-US"): string {
  const rtf = getRtf(locale);

  const targetDate = getMidnightDate(date);
  const currentDate = getMidnightDate(new Date());
  const deltaDays = Math.round(
    (targetDate.getTime() - currentDate.getTime()) / DAY_IN_MILLISECONDS
  );

  // Return relative format for dates within a week.
  if (Math.abs(deltaDays) <= DAYS_IN_WEEK) {
    const formatted = rtf.format(deltaDays, "days");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  // Return formatted date (with or without year based on current year).
  const isSameYear = targetDate.getFullYear() === currentDate.getFullYear();

  return date.toLocaleDateString(locale, {
    ...(isSameYear && { month: "short", day: "numeric" }),
  });
}

/**
 * Format an amount of seconds into a readable duration capped to days.
 * @param totalSeconds - The amount of seconds.
 * @param extended - Whether the readable duration includes all units or
 * skips some depending on the value.
 * | Extended |  Output  |   Units   |
 * |----------|----------|-----------|
 * | true     | Any      | d h min s |
 * | false    | >= 7d    | d         |
 * | false    | >= 24h   | d h       |
 * | false    | >= 10h   | h         |
 * @returns readable duration
 *
 * ## Example
 * ```typescript
 * formatSecondsToDuration(0) // => 0s
 * formatSecondsToDuration(60) // => 1min 0s
 * formatSecondsToDuration(3600) // => 1h 0min 0s
 * formatSecondsToDuration(86400, false) // => 1d 0h
 * ```
 */
export function formatSecondsToDuration(
  totalSeconds: number,
  extended = true
): string {
  const totalDays = Math.floor(totalSeconds / 86400);
  const remainderAfterDays = totalSeconds - totalDays * 86400;
  const hours = Math.floor(remainderAfterDays / 3600);
  const remainderAfterHours = remainderAfterDays - hours * 3600;
  const minutes = Math.floor(remainderAfterHours / 60);
  const seconds = remainderAfterHours - minutes * 60;

  if (totalDays) {
    if (extended) {
      return `${totalDays}d ${hours}h ${minutes}min ${seconds}s`;
    }
    if (totalDays >= 7) {
      return `${totalDays}d`;
    }
    return `${totalDays}d ${hours}h`;
  }

  if (hours) {
    if (hours >= 10 && !extended) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}min ${seconds}s`;
  }

  if (minutes) {
    return `${minutes}min ${seconds}s`;
  }

  return `${seconds}s`;
}
